import React, { useState } from 'react';
import { useModal } from 'hooks/useModal';
import { useFilesInput } from 'hooks/useFilesInput';
import { GeneralModal } from 'ui/GeneralModal';
import FluidButton from 'ui/FluidButton';
import { CircleIconButton } from 'ui/CircleIconButton';
import { TextInput } from 'ui/TextInput';
import { ContentDocument } from './ContentDocument';
import { IContentFileUpload } from '../types';
import XIcon from 'ui/Icons/tabler/x.component.svg';

interface ISupportingDocumentsDisplayProps {
  documents: IContentFileUpload[];
  isCreateMode?: boolean;
  onAddDocument: (file: File, displayName: string) => Promise<void>;
  onDeleteDocument: (index: number) => void;
  onDocumentClick?: (index: number) => void;
}

export const SupportingDocumentsDisplay = ({
  documents,
  isCreateMode = false,
  onAddDocument,
  onDeleteDocument,
  onDocumentClick,
}: ISupportingDocumentsDisplayProps) => {
  const [displayName, setDisplayName] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const addUploadModalOps = useModal();
  const filesInput = useFilesInput({
    accept: '', // Accept all file types for documents
  });

  const hasDocuments = documents.length >= 1;

  const handleModalClose = () => {
    filesInput.reset();
    setDisplayName('');
    addUploadModalOps.handleClose();
  };

  const handleUploadConfirm = async () => {
    const file = filesInput.files[0];
    if (!file || displayName === '') {
      return;
    }

    setIsUploading(true);
    try {
      await onAddDocument(file, displayName);
      handleModalClose();
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className="flex flex-row gap-2 border border-gray-40 border-dashed bg-brown-prime-10 p-2">
        {hasDocuments && (
          <div className="flex flex-wrap gap-2">
            {documents.map((document, documentIdx) => (
              <div className="relative" key={`${document.displayName}-${documentIdx}`}>
                <ContentDocument
                  className="h-[90px] w-[90px]"
                  document={document}
                  index={documentIdx}
                  onClick={onDocumentClick}
                />
                <CircleIconButton
                  className="absolute top-2 right-2"
                  IconComponent={XIcon}
                  iconSize="sm"
                  type="secondary"
                  onClick={() => {
                    onDeleteDocument(documentIdx);
                  }}
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2 justify-start min-w-[100px]">
          {isCreateMode ? (
            <div className="text-center">
              <p className="text-gray-600 text-xs mb-2 font-pt-sans">Please save the content item before adding documents</p>
              <FluidButton
                disabled={true}
                type="secondary"
                className="bg-gray-100 border border-gray-300 text-gray-500 cursor-not-allowed"
              >
                Attach File
              </FluidButton>
            </div>
          ) : (
            <FluidButton
              onClick={() => {
                addUploadModalOps.showModal();
              }}
              type="secondary"
            >
              Attach File
            </FluidButton>
          )}
        </div>
      </div>

      {/* Upload Modal - only show in edit mode */}
      {!isCreateMode && (
        <GeneralModal
          isOpen={addUploadModalOps.isOpen}
          onClose={handleModalClose}
          modalWindowClassName="p-4 text-black font-pt-sans w-[587px]"
          isCloseButtonVisible={false}
        >
        <h2 className="m-0 font-noe-display text-21px text-black font-normal mb-25px">Add File</h2>
        
        <span className="block">Display Name</span>
        <TextInput
          id="add-file-modal-display-name"
          value={displayName}
          onChange={e => {
            setDisplayName(e.currentTarget.value);
          }}
        />
        
        <span className="block mt-10px">File</span>
        {filesInput.renderInput()}

        <div className="flex flex-row gap-2 mt-25px">
          <FluidButton
            disabled={filesInput.files.length === 0 || displayName === '' || isUploading}
            type="primary"
            className="mt-10px"
            onClick={handleUploadConfirm}
          >
            {isUploading ? 'Uploading...' : 'Upload & Add File'}
          </FluidButton>
          <FluidButton type="secondary" className="mt-10px" onClick={handleModalClose}>
            Cancel
          </FluidButton>
        </div>
        </GeneralModal>
      )}
    </>
  );
};